import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";
import type { RawGrade } from "@/lib/marketplace";

/**
 * PSA equivalency for each Want To Buy raw condition tier — used as a
 * shared reference across the create-order helper, the offer submission
 * form, and this page. Not a Want To Buy warranty: buyers must evaluate
 * seller photos themselves.
 */
export const RAW_GRADE_PSA: Record<RawGrade, { range: string; short: string }> = {
  NM: { range: "PSA 8 to 10", short: "PSA 8+ equivalent" },
  LP: { range: "PSA 6 to 7", short: "PSA 6–7 equivalent" },
  MP: { range: "PSA 4 to 5", short: "PSA 4–5 equivalent" },
  HP: { range: "PSA 2 to 3", short: "PSA 2–3 equivalent" },
  DMG: { range: "PSA 1", short: "PSA 1 equivalent" },
};

/** Short helper text shown next to a condition selector on the offer form. */
export const RAW_GRADE_HELPER: Record<RawGrade, string> = {
  NM: "NM = PSA 8+ equivalent — buyers will scrutinise corner photos closely.",
  LP: "LP = PSA 6–7 equivalent — minor edge wear or light scuffs are acceptable.",
  MP: "MP = PSA 4–5 equivalent — moderate wear, light scratching, or soft creases.",
  HP: "HP = PSA 2–3 equivalent — major wear; card must still be authenticatable.",
  DMG: "DMG = PSA 1 equivalent — disclose all defects and alterations in your notes.",
};

/** Full condition definition — shared reference for buyers and sellers. */
export const RAW_GRADE_DEFINITION: Record<RawGrade, string> = {
  NM: "Near Mint (NM) — PSA 8+ equivalent. Minimal to no visible surface wear, crisp corners, clean edges, no creasing. Minor manufacturer print imperfections acceptable. Any visible play wear, whitening, or edge nicks downgrades to LP. Foil: minor manufacturer speckle acceptable provided it is not excessive.",
  LP: "Lightly Played (LP) — PSA 6 to 7 equivalent. Minor edge wear, light scuffs, or minimal scratching. No significant imperfections affecting structural integrity.",
  MP: "Moderately Played (MP) — PSA 4 to 5 equivalent. Edge wear, surface wear, light scratching, scuffing, minor bends with soft creases, or a combination of moderate examples.",
  HP: "Heavily Played (HP) — PSA 2 to 3 equivalent. Major wear including significant creasing, whitening, edge damage, or multiple moderate imperfections in combination. Must still be identifiable and authenticatable.",
  DMG: "Damaged (DMG) — Imperfections beyond HP. Major border wear, deep scratching, tears, splits, significant bends, or damage affecting structural integrity. Cards with foreign substances are NOT eligible for sale. Manufacturing defects and alterations are automatically DMG and must be disclosed.",
};

export const Route = createFileRoute("/condition-guide")({
  head: () => ({
    meta: [
      { title: "Raw Card Condition Guide — Want To Buy" },
      {
        name: "description",
        content:
          "A shared reference for describing raw ungraded trading card condition on Want To Buy — NM, LP, MP, HP, DMG — with PSA equivalencies.",
      },
      { property: "og:title", content: "Raw Card Condition Guide — Want To Buy" },
      {
        property: "og:description",
        content:
          "Shared condition tiers for raw cards on Want To Buy with PSA equivalencies. Buyers evaluate seller photos themselves.",
      },
    ],
  }),
  component: ConditionGuidePage,
});

export function ConditionGuideContent() {
  return (
    <>
      <p className="!mt-0 text-xs uppercase tracking-wider text-muted-foreground">
        Last updated: July 2026 · Shared reference — Want To Buy does not verify condition claims
      </p>

      <section>
        <h2>How raw card condition is described on Want To Buy</h2>
        <p>
          Every raw ungraded card on Want To Buy is described using one of five condition tiers — Near Mint (NM), Lightly Played (LP), Moderately Played (MP), Heavily Played (HP), or Damaged (DMG). These tiers are mapped to PSA grade equivalents so buyers and sellers share a common vocabulary.
        </p>
        <p>
          <strong>Want To Buy does not verify condition claims.</strong> This page is a shared reference; the buyer is solely responsible for evaluating the seller's timestamped photos and any additional photos they request before proceeding with a transaction.
        </p>
      </section>

      <section>
        <h2>PSA equivalency table</h2>
        <div className="overflow-x-auto !mt-3">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-panel/60 text-left">
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tier</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Full name</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">PSA equivalent</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border"><td className="px-3 py-2 font-bold text-emerald-400">NM</td><td className="px-3 py-2">Near Mint</td><td className="px-3 py-2">PSA 8 to 10</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 font-bold text-amber-400">LP</td><td className="px-3 py-2">Lightly Played</td><td className="px-3 py-2">PSA 6 to 7</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 font-bold text-orange-400">MP</td><td className="px-3 py-2">Moderately Played</td><td className="px-3 py-2">PSA 4 to 5</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 font-bold text-rose-400">HP</td><td className="px-3 py-2">Heavily Played</td><td className="px-3 py-2">PSA 2 to 3</td></tr>
              <tr><td className="px-3 py-2 font-bold text-destructive">DMG</td><td className="px-3 py-2">Damaged</td><td className="px-3 py-2">PSA 1</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>Near Mint (NM) — PSA 8+ equivalent</h2>
        <p>Minimal to no visible surface wear, crisp corners, clean edges, no creasing. Minor print imperfections from manufacturing are acceptable. Any visible play wear, whitening, or edge nicks downgrades to Lightly Played.</p>
        <h3>NM Foil</h3>
        <p>NM Foil cards are evaluated by the same criteria; minor foil speckle from manufacturing is acceptable at NM provided it is not excessive.</p>
      </section>

      <section>
        <h2>Lightly Played (LP) — PSA 6 to 7 equivalent</h2>
        <p>Minor edge wear, light scuffs, or minimal scratching. No significant imperfections affecting structural integrity.</p>
      </section>

      <section>
        <h2>Moderately Played (MP) — PSA 4 to 5 equivalent</h2>
        <p>Edge wear, surface wear, light scratching, scuffing, minor bends with soft creases, or a combination of moderate examples. Minor bends or soft peeling are acceptable at MP provided they do not affect structural integrity.</p>
      </section>

      <section>
        <h2>Heavily Played (HP) — PSA 2 to 3 equivalent</h2>
        <p>Major wear including significant creasing, whitening, edge damage, or multiple moderate imperfections in combination. HP cards must still be identifiable and authenticatable.</p>
      </section>

      <section>
        <h2>Damaged (DMG) — PSA 1 equivalent</h2>
        <p>Cards with imperfections beyond HP standards — major border wear, deep scratching, tears, splits, significant bends, or damage affecting structural integrity.</p>
        <div className="panel-note">
          <strong>Foreign substances:</strong> Cards with food, liquid, stickers, or other foreign material are <strong>not eligible for sale on Want To Buy under any condition tier</strong>.
        </div>
      </section>

      <section>
        <h2>Defects and alterations</h2>
        <ul>
          <li><strong>Manufacturing defects</strong> such as significant miscuts or crimping are automatically classified as Damaged.</li>
          <li><strong>Alterations</strong> including stamps, signatures, paint, or marker are also classified as Damaged.</li>
          <li>Sellers listing defected or altered cards must <strong>disclose this explicitly</strong> in their offer notes and submit clear photos of the defect.</li>
        </ul>
      </section>

      <section>
        <h2>The mandatory 2-photo scam filter</h2>
        <p>
          Before the buyer and seller are connected through the message thread, the seller must submit 2 timestamped photos — front and back of the card, each with a handwritten note in the same frame containing the current date (DD/MM/YYYY), <code>Seller:</code>, <code>Buyer:</code>, order ID, and card condition. See the <a href="/seller-policy">Seller Policy</a> for the full definition.
        </p>
        <p>
          <strong>The mandatory photos filter out sellers who do not physically hold the card.</strong> They are not a Want To Buy grading service — buyers must evaluate condition themselves and may request additional photos through the message thread.
        </p>
      </section>

      <section>
        <h2>Foil cards</h2>
        <p>Foil cards are evaluated by the same criteria as non-foil cards. Foil bubbling and excessive foil speckle may affect condition and should be photographed and disclosed by sellers.</p>
      </section>
    </>
  );
}

function ConditionGuidePage() {
  return (
    <DocPage
      eyebrow="Shared Reference"
      title="Raw Card Condition Guide"
      intro="A shared vocabulary for describing raw ungraded cards on Want To Buy. Buyers evaluate seller photos themselves — Want To Buy does not verify condition claims."
    >
      <ConditionGuideContent />
    </DocPage>
  );
}
